'use client';

import { useEffect, useState } from 'react';

import { getPlans } from '../../../../services/products.service';
import { COILMIND_PLANS, mergePlans } from './plan';

/**
 * The plan catalogue with live prices.
 *
 * Prices used to live in two places — this file's catalogue and the server's
 * products table — and they drifted: a card read €59 while the tier-change
 * preview on the very next screen read €3. Everything a customer sees as a
 * number now comes from GET /products/plans; the catalogue keeps only what is
 * not a price (limits, devices, speed, copy, order, the recommended flag).
 *
 * The fetch is shared process-wide rather than per-component: the landing
 * renders three cards and the account page renders its own picker, and none of
 * them should cost a separate request.
 *
 * Failure is not an error state. The catalogue's own numbers render instead, so
 * a pricing page never appears empty or blocked — `source` says which is on
 * screen, for anything that wants to know.
 */
let inFlight = null;
let cached = null;

const loadPlans = () => {
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = getPlans()
      .then((plans) => {
        cached = mergePlans(plans);
        return cached;
      })
      .catch(() => {
        // Leave `cached` unset so a later mount can try again — a transient
        // failure should not pin the page to fallback prices for the session.
        inFlight = null;
        return COILMIND_PLANS;
      });
  }
  return inFlight;
};

export function usePlans() {
  const [plans, setPlans] = useState(cached || COILMIND_PLANS);
  const [source, setSource] = useState(cached ? 'server' : 'fallback');

  useEffect(() => {
    let alive = true;
    loadPlans().then((resolved) => {
      if (!alive) return;
      setPlans(resolved);
      setSource(resolved === COILMIND_PLANS ? 'fallback' : 'server');
    });
    return () => { alive = false; };
  }, []);

  return { plans, source };
}

// Test seam: lets a suite start from a known state.
export const __resetPlanCache = () => { inFlight = null; cached = null; };
